import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { Checkbook } from '@/interfaces/checkbook';
import checkbookServices from '@/api/checkbookService';

interface FilterParams {
  sort: string | undefined;
  page: number | undefined;
  size: number | undefined;
}

interface MetaData {
  totalPages: number;
  totalElements: number;
}

interface CheckbooksState {
  checkbooks: { [key: string]: Checkbook[] };
  selectedCheckbook: Checkbook;
  loading: boolean;
  error: string;
  currFilters: string;
  metaData: { [key: string]: MetaData };
  params: FilterParams;
}

const initialState: CheckbooksState = {
  checkbooks: {},
  selectedCheckbook: {} as Checkbook,
  loading: false,
  error: '',
  currFilters: '',
  metaData: {},
  params: {
    sort: undefined,
    page: undefined,
    size: undefined,
  },
};

// IB1-300 (épique #5428, F1.2/F5.1) : consomme désormais l'historique complet des demandes
// (cf. checkbookService.getCheckbooks). Le tri décroissant par date de création reste porté par
// le paramètre `sort` (défaut 'createdAt,desc' côté composant) — RM-02 du cahier des charges.
export const fetchCheckbooks = createAsyncThunk(
  'checkbook/checkbooks',
  async ({ size, page, sort }: { size: number; page: number; sort: string }, thunkAPI) => {
    try {
      const result = await checkbookServices.getCheckbooks({ size, page: page - 1, sort });
      return result;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.data);
    }
  }
);

export const checkbookSlice = createSlice({
  name: 'checkbook',
  initialState,
  reducers: {
    refresh: () => initialState,
    handleParamsChange: (state, action: PayloadAction<{ size: number; page: number; sort: string }>) => {
      const checkbookFilters = JSON.stringify(action.payload);
      state.currFilters = checkbookFilters;
      state.params.sort = action.payload.sort;
      state.params.page = action.payload.page;
      state.params.size = action.payload.size;
    },
    updateCheckbook: (state, action: PayloadAction<{ id: string; checkbook: Checkbook }>) => {
      const { id, checkbook } = action.payload;

      Object.keys(state.checkbooks).forEach(key => {
        state.checkbooks[key].forEach((chbook, index) => {
          if (chbook.id === id) state.checkbooks[key][index] = checkbook;
        });
      });
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchCheckbooks.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchCheckbooks.fulfilled, (state, action) => {
      const { payload: data } = action;
      state.checkbooks[state.currFilters] = data.data;
      state.metaData[state.currFilters] = { totalPages: data.totalPages, totalElements: data.totalElements };
      state.loading = false;
    });
    builder.addCase(fetchCheckbooks.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });
  },
});

export const { handleParamsChange, refresh, updateCheckbook } = checkbookSlice.actions;

export const selectCheckbookData = (state: RootState) => state.checkbook;

export default checkbookSlice.reducer;
